import type { ReactNode } from 'react';
import { capacityLabel, isCapacityFull } from '../../components/plan/planLabels';
import type { KbCapacityItem } from '../../types/api';

/** Fill share at which the storage bar turns amber. */
const CAPACITY_WARN_RATIO = 0.8;

/** "3 of 3 · Solo allows 10" beside the title; storage adds a thin bar. */
function CapacityMeta({ capacity, isBytes }: { capacity: KbCapacityItem; isBytes: boolean }) {
  const full = isCapacityFull(capacity);
  const ratio =
    capacity.limit === null || capacity.limit <= 0
      ? null
      : Math.min(1, capacity.count / capacity.limit);
  const tone = full
    ? ' kb-capacity--full'
    : ratio !== null && ratio >= CAPACITY_WARN_RATIO
      ? ' kb-capacity--warn'
      : '';

  return (
    <div className={`kb-capacity${tone}`}>
      <span className="kb-capacity__label">{capacityLabel(capacity, isBytes)}</span>
      {isBytes && ratio !== null ? (
        <span className="kb-capacity__bar" aria-hidden="true">
          <span className="kb-capacity__fill" style={{ width: `${ratio * 100}%` }} />
        </span>
      ) : null}
    </div>
  );
}

interface SectionShellProps {
  title: string;
  lede: string;
  /** Primary action for the section, usually "Add …". */
  action?: ReactNode;
  /** Status under the header that is not an error (brand memory's last run). */
  aside?: ReactNode;
  /**
   * How full the section is against the plan's cap. Omitted (or null while
   * unknown) shows nothing. The section never hides at the cap; the caller
   * disables its own add action.
   */
  capacity?: KbCapacityItem | null;
  /** `capacity` counts bytes (Images & logos), not rows. */
  capacityInBytes?: boolean;
  isLoading: boolean;
  error: string | null;
  isEmpty: boolean;
  /** Invitation to act, not an apology. */
  emptyMessage: string;
  /** Verb-first CTA shown inside the empty state, wired to the same create handler. */
  emptyAction?: ReactNode;
  children: ReactNode;
}

/** Shared chrome for every knowledge-base section: header, states, list slot. */
export function SectionShell({
  title,
  lede,
  action,
  aside,
  capacity = null,
  capacityInBytes = false,
  isLoading,
  error,
  isEmpty,
  emptyMessage,
  emptyAction,
  children,
}: SectionShellProps) {
  return (
    <section className="kb-section">
      <header className="kb-section__header">
        <div>
          <div className="kb-section__title-row">
            <h2 className="kb-section__title">{title}</h2>
            {capacity ? <CapacityMeta capacity={capacity} isBytes={capacityInBytes} /> : null}
          </div>
          <p className="kb-section__lede">{lede}</p>
        </div>
        {action}
      </header>

      {aside}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      {isLoading ? (
        <p className="kb-section__status" role="status">
          Loading
        </p>
      ) : isEmpty ? (
        <div className="kb-section__empty">
          <p className="kb-section__empty-text">{emptyMessage}</p>
          {emptyAction}
        </div>
      ) : (
        children
      )}
    </section>
  );
}
